'use client';

import { useCallback, useState, type KeyboardEvent } from 'react';
import { IconButton } from '../../../components/icon-button';
import { Input } from '../../../components/input';
import { Menu } from '../../../components/menu';
import { BodyDefault, MetaDefault } from '../../../components/typography';
import { Box, HStack, token } from '@terra/ui-styled-system';
import {
  Comment01Icon,
  Delete02Icon,
  Edit03Icon,
  Loading03Icon,
  MoreHorizontalIcon,
} from '@terra/ui-icons';
import { resolveRenamedConversationTitle } from './resolve-renamed-conversation-title';
import type { ConversationHistoryLabels } from './types';

type ConversationHistoryRowProps = {
  title: string;
  isActive: boolean;
  isStreaming?: boolean;
  updatedAt?: string;
  labels: Pick<
    ConversationHistoryLabels,
    | 'formatUpdatedAt'
    | 'deleteConversationAriaLabel'
    | 'renameLabel'
    | 'deleteLabel'
    | 'moreActionsAriaLabel'
  >;
  onSelect: () => void;
  onDelete: () => void;
  onRename: (title: string) => void | Promise<void>;
};

export function ConversationHistoryRow({
  title,
  isActive,
  isStreaming,
  updatedAt,
  labels,
  onSelect,
  onDelete,
  onRename,
}: ConversationHistoryRowProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTitle, setEditingTitle] = useState(title);
  const [isSaving, setIsSaving] = useState(false);
  const showMeta = (isHovered || isMenuOpen) && !isEditing;

  const handleStartRename = useCallback(() => {
    setEditingTitle(title);
    setIsEditing(true);
  }, [title]);

  const handleCancelRename = useCallback(() => {
    setIsEditing(false);
    setEditingTitle(title);
  }, [title]);

  const handleSaveRename = useCallback(async () => {
    const nextTitle = resolveRenamedConversationTitle({
      nextTitle: editingTitle,
      currentTitle: title,
    });

    if (!nextTitle) {
      handleCancelRename();
      return;
    }

    setIsSaving(true);

    try {
      await onRename(nextTitle);
      setIsEditing(false);
    } catch {
      setEditingTitle(title);
    } finally {
      setIsSaving(false);
    }
  }, [editingTitle, handleCancelRename, onRename, title]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void handleSaveRename();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        handleCancelRename();
      }
    },
    [handleCancelRename, handleSaveRename],
  );

  return (
    <Menu.Root
      open={isMenuOpen}
      onOpenChange={(details) => setIsMenuOpen(details.open)}
      positioning={{ strategy: 'fixed', placement: 'bottom-end' }}
    >
      <Menu.ContextTrigger asChild>
        <Box
          role="button"
          tabIndex={0}
          w="full"
          minW={0}
          onClick={() => {
            if (!isEditing) {
              onSelect();
            }
          }}
          onKeyDown={(event) => {
            if (isEditing) {
              return;
            }

            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onSelect();
            }
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          textAlign="left"
          border="none"
          borderRadius="md"
          cursor="pointer"
          bg={isActive ? 'bg.muted' : 'transparent'}
          _hover={{ bg: 'bg.subtle' }}
          px="2"
          py="1.5"
        >
          <HStack gap="2" w="full" minW={0} alignItems="center" overflow="hidden">
            <Box flexShrink={0} display="inline-flex">
              {isStreaming || isSaving ? (
                <Box
                  as="span"
                  display="inline-flex"
                  flexShrink={0}
                  animationStyle="spin"
                  animationDuration="1.5s"
                >
                  <Loading03Icon size={12} color={token('colors.terra.a9')} />
                </Box>
              ) : (
                <Comment01Icon size={12} opacity={0.7} />
              )}
            </Box>
            <Box flex="1" minW={0} overflow="hidden">
              {isEditing ? (
                <Input
                  value={editingTitle}
                  onChange={(event) => setEditingTitle(event.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={() => {
                    void handleSaveRename();
                  }}
                  onClick={(event) => event.stopPropagation()}
                  size="xs"
                  autoFocus
                  disabled={isSaving}
                />
              ) : (
                <BodyDefault as="span" display="block" truncate>
                  {title}
                </BodyDefault>
              )}
            </Box>
            {showMeta ? (
              <HStack gap="1" flexShrink={0} alignItems="center">
                {updatedAt ? (
                  <MetaDefault color="fg.muted" whiteSpace="nowrap">
                    {labels.formatUpdatedAt(updatedAt)}
                  </MetaDefault>
                ) : null}
                <Menu.Trigger asChild>
                  <IconButton
                    variant="link"
                    size="xs"
                    aria-label={labels.moreActionsAriaLabel}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    color="fg.muted"
                    _hover={{ color: 'fg.default' }}
                  >
                    <MoreHorizontalIcon size={12} />
                  </IconButton>
                </Menu.Trigger>
                <IconButton
                  variant="link"
                  size="xs"
                  aria-label={labels.deleteConversationAriaLabel}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete();
                  }}
                  color="fg.muted"
                  _hover={{ color: 'fg.default' }}
                >
                  <Delete02Icon size={12} />
                </IconButton>
              </HStack>
            ) : null}
          </HStack>
        </Box>
      </Menu.ContextTrigger>
      <Menu.Positioner>
        <Menu.Content minW="140px" py="1">
          <Menu.Item
            value="rename"
            py="1"
            px="2"
            onClick={(event) => {
              event.stopPropagation();
              handleStartRename();
            }}
          >
            <HStack gap="1.5">
              <Edit03Icon size={12} />
              <MetaDefault>{labels.renameLabel}</MetaDefault>
            </HStack>
          </Menu.Item>
          <Menu.Item
            value="delete"
            py="1"
            px="2"
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
          >
            <HStack gap="1.5">
              <Delete02Icon size={12} />
              <MetaDefault>{labels.deleteLabel}</MetaDefault>
            </HStack>
          </Menu.Item>
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
}
